"use client";

import { useAppSelector } from "@/store/hooks";
import { Button } from "@/ui";
import Image from "next/image";

export interface CTASideBySideProps {
  onPrimaryClick?: () => void;
  onSecondaryClick?: () => void;
}

export default function CTASideBySide({
  onPrimaryClick,
  onSecondaryClick,
}: CTASideBySideProps) {
  const ctaContent = useAppSelector(
    (state) => state.content.data?.ctaSideBySide,
  );
  const loading = useAppSelector((state) => state.content.loading);

  // Loading state or no content
  if (!ctaContent) {
    return (
      <section className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center rounded-3xl bg-white/50 backdrop-blur p-8 border border-black/10">
          <div className="animate-pulse space-y-4">
            <div className="h-10 bg-black/10 rounded w-3/4" />
            <div className="h-4 bg-black/10 rounded w-full" />
            <div className="flex gap-4">
              <div className="h-12 bg-black/10 rounded w-32" />
              <div className="h-12 bg-black/10 rounded w-32" />
            </div>
          </div>
          <div className="h-64 md:h-72 bg-black/10 rounded-2xl animate-pulse" />
        </div>
      </section>
    );
  }

  return (
    <section className="max-w-7xl mx-auto px-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center rounded-3xl bg-white/50 backdrop-blur p-8 border border-black/10">
        <div>
          <h3 className="text-4xl md:text-5xl font-bold text-black mb-4">
            {ctaContent?.title}
          </h3>
          <p className="text-lg text-black/70 mb-6">{ctaContent?.subtitle}</p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              type="button"
              variant="primary"
              size="lg"
              onClick={onPrimaryClick}
              className="rounded-full"
            >
              {ctaContent?.primaryLabel}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="lg"
              onClick={onSecondaryClick}
              className="rounded-full"
            >
              {ctaContent?.secondaryLabel}
            </Button>
          </div>
        </div>
        <div className="relative h-64 md:h-72 rounded-2xl overflow-hidden">
          <Image
            src={ctaContent?.imageSrc}
            alt={ctaContent?.imageAlt}
            fill
            className="object-cover"
            unoptimized
          />
        </div>
      </div>
    </section>
  );
}
